import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Link as PdsLink, Text } from '@puppet/react-components';
import { Authentication } from '@puppet/react-layouts';
import CopyrightFooter from '@components/CopyrightFooter';
import FormError from '@components/FormError';
import { useSignUpV1 } from '@services/cd4pe/users';
import { LINKS } from 'src/routes';
import { checkPasswordForSignup } from 'src/utils/passwordPolicy';
import SignUpForm, { SignUpFormValues } from './components/SignUpForm';

type PdsFormError = {
  message?: string;
  causes?: Array<string | Error | { message?: string; causes?: unknown[] }>;
  items?: Record<string, string>;
};

const SignUp = () => {
  const { t } = useTranslation('codeDelivery');
  const navigate = useNavigate();
  const signUp = useSignUpV1();

  const [formError, setFormError] = useState<PdsFormError | undefined>(
    undefined,
  );

  const policyText = useMemo(
    () => ({
      length: t('signup.form.field.password.policy.length'),
      upper: t('signup.form.field.password.policy.upper'),
      lower: t('signup.form.field.password.policy.lower'),
      number: t('signup.form.field.password.policy.number'),
      special: t('signup.form.field.password.policy.special'),
    }),
    [t],
  );

  const clearFormError = () => setFormError(undefined);

  const onSubmit = ({
    firstName,
    lastName,
    companyName,
    username,
    email,
    password,
    confirmPassword,
  }: SignUpFormValues) => {
    clearFormError();

    const policy = checkPasswordForSignup(password);

    if (!policy.ok) {
      const missingBullets = policy.missing.map((key) => policyText[key]);

      setFormError({
        message: t('signup.form.field.password.policy.summary'),
        causes: missingBullets,
        items: {
          password: t('signup.form.field.password.policy.inline'),
        },
      });
      return;
    }
    if (password !== confirmPassword) {
      setFormError({
        message: t('signup.form.field.password.errorSummary'),
        items: {
          confirmPassword: t('signup.form.field.password.error'),
        },
      });
      return;
    }

    signUp.mutate(
      {
        firstName: firstName!,
        lastName: lastName!,
        username,
        email,
        passwd: password,
        passwd2: confirmPassword,
        ...(companyName && { companyName }),
      },
      {
        onSuccess: () => {
          navigate(
            LINKS.username.chooseWorkspace({
              path: { username },
            }),
          );
        },
      },
    );
  };

  return (
    <Authentication
      className="signup"
      product="continuous-delivery"
      title={t('signup.header')}
      footer={<CopyrightFooter />}
    >
      <Text className="signup__description" color="medium">
        {t('signup.description')}
      </Text>
      <SignUpForm
        formError={formError}
        isLoading={signUp.isLoading}
        onSubmit={onSubmit}
      />
      {signUp.error && <FormError message={signUp.error.toString()} />}
      <div className="signup__footer">
        <Trans
          t={t}
          i18nKey="signup.links.signIn"
          components={[
            <Text color="medium" />,
            <PdsLink as={Link} to={LINKS.login({})} />,
          ]}
        />
      </div>
    </Authentication>
  );
};

export default SignUp;
